import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useBoard } from '@/features/crm/use-crm';
import { formatCurrency } from '@/lib/utils';

/** Pipeline health at a glance, with the weighted forecast front and centre. */
export function PipelineSummaryCard() {
  const board = useBoard({ limitPerStage: 1 });

  if (board.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (board.isError || !board.data) return null;

  const { totals, currency, columns } = board.data;
  const openColumns = columns.filter((column) => !column.stage.isWon && !column.stage.isLost);
  const largest = Math.max(...openColumns.map((column) => Number(column.totalValue)), 1);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>Pipeline</CardTitle>
          <CardDescription>{board.data.pipeline.name}</CardDescription>
        </div>
        <Link
          to="/deals"
          className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
        >
          Open board
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Open value
            </p>
            <p className="text-2xl font-semibold text-ink">
              {formatCurrency(Number(totals.openValue), currency)}
            </p>
            <p className="text-xs text-ink-subtle">{totals.openCount} deals</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Weighted forecast
            </p>
            <p className="text-2xl font-semibold text-brand-ink">
              {formatCurrency(Number(totals.weightedValue), currency)}
            </p>
            <p className="text-xs text-ink-subtle">by close probability</p>
          </div>
        </div>

        {/* A bar per stage: proportion is easier to read than a column of numbers. */}
        <ul className="space-y-2">
          {openColumns.map((column) => (
            <li key={column.stage.id} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-ink-muted">{column.stage.name}</span>
                <span className="tabular-nums text-ink">
                  {formatCurrency(Number(column.totalValue), currency)}
                </span>
              </div>
              <div
                className="h-1.5 overflow-hidden rounded-full bg-surface-sunken"
                role="img"
                aria-label={`${column.stage.name}: ${column.totalCount} deals`}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(2, (Number(column.totalValue) / largest) * 100)}%`,
                    backgroundColor: column.stage.color ?? 'var(--color-brand)',
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
